import { forwardRef, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import {
  endOfMonth,
  startOfMonth,
  subDays,
  subMonths,
  startOfDay,
  endOfDay,
} from 'date-fns';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';

type DatePreset = {
  label: string;
  value: DateRange;
};

type CalendarRangePickerProps = {
  /**
   * The selected date range
   */
  value?: DateRange;
  /**
   * Callback fired when the date range changes
   */
  onValueChange?: (range: DateRange | undefined) => void;
  /**
   * Callback fired when a preset is selected
   */
  onPresetSelect?: (preset: DatePreset) => void;
  /**
   * The initial date range when component is uncontrolled
   */
  defaultValue?: DateRange;
  /**
   * Custom date presets to show in the sidebar
   */
  presets?: DatePreset[];
  /**
   * Whether to show the presets sidebar
   */
  showPresets?: boolean;
  /**
   * Custom class name for the root element
   */
  className?: string;
  /**
   * Whether the calendar is disabled
   */
  disabled?: boolean;
  /**
   * Minimum selectable date
   */
  fromDate?: Date;
  /**
   * Maximum selectable date (defaults to today)
   */
  toDate?: Date;
  /**
   * Number of months to display
   */
  numberOfMonths?: number;
};

const CalendarRangePicker = forwardRef<HTMLDivElement, CalendarRangePickerProps>(
  (
    {
      value,
      onValueChange,
      onPresetSelect,
      defaultValue,
      presets,
      showPresets = true,
      className,
      disabled = false,
      fromDate,
      toDate,
      numberOfMonths = 1,
      ...props
    },
    ref,
  ) => {
    const today = new Date();
    // Set toDate to today by default to disable future dates
    const effectiveToDate = toDate ?? today;

    // Default presets
    const defaultPresets: DatePreset[] = useMemo(
      () => [
        {
          label: 'Today',
          value: {
            from: startOfDay(today),
            to: endOfDay(today),
          },
        },
        {
          label: 'Yesterday',
          value: {
            from: startOfDay(subDays(today, 1)),
            to: endOfDay(subDays(today, 1)),
          },
        },
        {
          label: 'Last 7 days',
          value: {
            from: startOfDay(subDays(today, 6)),
            to: endOfDay(today),
          },
        },
        {
          label: 'Last 30 days',
          value: {
            from: startOfDay(subDays(today, 29)),
            to: endOfDay(today),
          },
        },
        {
          label: 'Month to date',
          value: {
            from: startOfDay(startOfMonth(today)),
            to: endOfDay(today),
          },
        },
        {
          label: 'Last month',
          value: {
            from: startOfDay(startOfMonth(subMonths(today, 1))),
            to: endOfDay(endOfMonth(subMonths(today, 1))),
          },
        },
      ],
      [today],
    );

    const effectivePresets = presets ?? defaultPresets;
    const defaultRange = defaultValue ?? defaultPresets[2].value; // Last 7 days

    const [internalValue, setInternalValue] = useState<DateRange | undefined>(
      value ?? defaultRange,
    );
    const [month, setMonth] = useState<Date>(
      value?.to ?? defaultRange?.to ?? today,
    );

    const selectedRange = value ?? internalValue;
    const isControlled = value !== undefined;

    const handleRangeChange = (range: DateRange | undefined) => {
      if (disabled)
        return;

      // Normalize the range to proper day boundaries
      const normalizedRange = range
        ? {
            from: range.from ? startOfDay(range.from) : undefined,
            to: range.to ? endOfDay(range.to) : (range.from ? endOfDay(range.from) : undefined),
          }
        : undefined;

      if (!isControlled) {
        setInternalValue(normalizedRange);
      }
      onValueChange?.(normalizedRange);

      // Update visible month when range changes
      if (normalizedRange?.to) {
        setMonth(normalizedRange.to);
      }
    };

    const handlePresetClick = (preset: DatePreset) => {
      if (disabled)
        return;

      handleRangeChange(preset.value);
      setMonth(preset.value.to ?? today);
      onPresetSelect?.(preset);
    };

    return (
      <div
        ref={ref}
        className={cn('rounded-md border', className)}
        {...props}
      >
        <div className="flex max-sm:flex-col">
          {showPresets && (
            <div className="relative py-4 max-sm:order-1 max-sm:border-t sm:w-32">
              <div className="h-full sm:border-e">
                <div
                  className="flex flex-col px-2"
                  role="group"
                  aria-label="Date range presets"
                >
                  {effectivePresets.map((preset, index) => {
                    const isSelected
                      = selectedRange?.from?.getTime() === preset.value.from?.getTime()
                        && selectedRange?.to?.getTime() === preset.value.to?.getTime();

                    return (
                      <Button
                        key={`${preset.label}-${index}`}
                        variant={isSelected ? 'secondary' : 'ghost'}
                        size="sm"
                        className="w-full justify-start"
                        onClick={() => handlePresetClick(preset)}
                        disabled={disabled}
                        aria-pressed={isSelected}
                      >
                        {preset.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <div className="flex-1 max-sm:order-2">
            <Calendar
              mode="range"
              defaultMonth={month}
              month={month}
              onMonthChange={setMonth}
              selected={selectedRange}
              onSelect={handleRangeChange}
              numberOfMonths={numberOfMonths}
              disabled={disabled
                ? true
                : [
                    ...(fromDate ? [{ before: fromDate }] : []),
                    { after: effectiveToDate },
                  ]}
              hidden={[
                ...(fromDate ? [{ before: fromDate }] : []),
              ]}
              aria-label="Select date range"
            />
          </div>
        </div>
      </div>
    );
  },
);

CalendarRangePicker.displayName = 'CalendarRangePicker';

export { CalendarRangePicker, type CalendarRangePickerProps, type DatePreset };
export default CalendarRangePicker;
